import { Popover } from "../ui/floating.js";
import { Icon } from "../ui/icons.js";
import { Button } from "../ui/primitives.js";

/**
 * What the design canvas is actually showing.
 *
 * The canvas mixes four different things at once — data tokens that have not
 * been substituted, values that have, content a rule is currently hiding, and
 * tables cut down to a few rows for speed — and never says which is which. Put
 * on the canvas that would be four permanent captions competing with the report;
 * behind one button it is a page you read once and then stop needing.
 *
 * None of it is part of the document: every badge and outline is styled by the
 * editor and drawn only in design mode, so preview and the exported PDF never
 * see any of it.
 */
export function DesignLegend({ sampleLabel }: { sampleLabel: string }) {
  return (
    <Popover
      placement="top-end"
      className="bpe-legend"
      trigger={<Button size="sm" variant="ghost" icon="info" tooltip="What am I looking at?" aria-label="What am I looking at?" />}
    >
      <div className="bpe-legend__body">
        <h3 className="bpe-legend__title">Reading the design view</h3>
        <p className="bpe-legend__intro">
          This is the template, filled in from <strong>{sampleLabel}</strong> so you can see the shape of a real report. Switch to Preview for exactly what the PDF will contain.
        </p>
        <dl className="bpe-legend__list">
          <Entry swatch={<span className="bp-token bpe-legend__token">Client › Full name</span>} term="A blue chip is a data field">
            It shows which field is bound there, not the value. In Preview it becomes the value.
          </Entry>
          <Entry swatch={<Icon name="git-branch" size={14} />} term="A dashed outline is conditional">
            Content shown or hidden by a rule. The badge says which way the rule falls for this sample; hidden content still prints for a client the rule matches.
          </Entry>
          <Entry swatch={<Icon name="table" size={14} />} term="Tables and repeaters are shortened">
            The badge says how many of the sample's rows are drawn. Totals are always calculated over the whole list, not the visible rows.
          </Entry>
          <Entry swatch={<Icon name="pencil" size={14} />} term="Text is edited in place">
            Select a block and use <strong>Edit text</strong>, or double-click it. Drag a field from the Data panel to drop a token into a sentence.
          </Entry>
        </dl>
        <p className="bpe-legend__foot">None of these markings exist in the exported document.</p>
      </div>
    </Popover>
  );
}

function Entry({ swatch, term, children }: { swatch: React.ReactNode; term: string; children: React.ReactNode }) {
  return (
    <div className="bpe-legend__entry">
      <div className="bpe-legend__swatch">{swatch}</div>
      <div>
        <dt>{term}</dt>
        <dd>{children}</dd>
      </div>
    </div>
  );
}
